import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { PORTAL_LINKS, SITE_PHONE_DISPLAY, SITE_PHONE_TEL } from '../constants/site';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [audience, setAudience] = useState('personal');
  const [showMobileSticky, setShowMobileSticky] = useState(false);
  const location = useLocation();

  const personalPaths = ['/car-loans', '/personal-loans', '/leisure-finance', '/vehicle-finance'];
  const businessPaths = ['/truck-and-trailer-finance', '/equipment-finance', '/business-loans', '/vehicle-finance', '/locations'];

  useEffect(() => {
    const path = location.pathname;
    if (businessPaths.some((item) => path === item || path.startsWith(`${item}/`))) {
      setAudience('business');
      return;
    }
    if (personalPaths.some((item) => path === item || path.startsWith(`${item}/`))) {
      setAudience('personal');
    }
  }, [location.pathname]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setShowMobileSticky(window.scrollY > 520);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const personalLinks = useMemo(
    () => [
      { name: 'Car Finance', path: '/car-loans', aliases: ['/car-loans'] },
      { name: 'Personal Loans', path: '/personal-loans', aliases: ['/personal-loans'] },
      { name: 'Leisure Finance', path: '/leisure-finance', aliases: ['/leisure-finance'] },
    ],
    []
  );

  const businessLinks = useMemo(
    () => [
      { name: 'Asset Finance', path: '/vehicle-finance', aliases: ['/vehicle-finance'] },
      { name: 'Commercial Loans', path: '/business-loans', aliases: ['/business-loans', '/business-finance'] },
      { name: 'Equipment Finance', path: '/equipment-finance', aliases: ['/equipment-finance'] },
    ],
    []
  );

  const navLinks = audience === 'business' ? businessLinks : personalLinks;
  const utilityLinks = [
    { name: 'Calculators', path: '/calculators', aliases: ['/calculators'] },
    { name: 'Learn', path: '/learn', aliases: ['/learn'] },
  ];

  const isActive = (aliases) => aliases.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-purple-200 bg-white/95 shadow-sm backdrop-blur-xl" data-testid="site-header">
        <div className="mx-auto flex h-[68px] w-full max-w-7xl items-center justify-between gap-2 px-3 sm:h-20 sm:px-6 lg:px-8" data-testid="header-main-row">
          <Link to="/" className="flex shrink-0 items-center" data-testid="logo-link">
            <div className="h-8 w-[102px] overflow-hidden sm:h-10 sm:w-[128px] md:h-11 md:w-[134px] lg:h-[60px] lg:w-[142px]" data-testid="header-brand-logo-wrap">
              <img
                src="https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/i5qmq2zt_Copy%20of%20Flash%20Funding.png"
                alt="Flash Funding"
                className="h-full w-full object-cover object-[43%_50%]"
                data-testid="header-brand-logo"
              />
            </div>
          </Link>

          <div className="hidden items-center rounded-xl border border-purple-200 bg-[#f8f5fc] p-1 lg:flex" data-testid="header-audience-toggle-wrap">
            <button
              type="button"
              onClick={() => setAudience('personal')}
              className={`h-9 rounded-lg px-4 text-xs font-semibold transition-[background-color,color] ${
                audience === 'personal' ? 'bg-white text-purple-700 shadow-sm' : 'text-[#5B5570] hover:text-purple-700'
              }`}
              data-testid="header-toggle-personal"
            >
              Personal
            </button>
            <button
              type="button"
              onClick={() => setAudience('business')}
              className={`h-9 rounded-lg px-4 text-xs font-semibold transition-[background-color,color] ${
                audience === 'business' ? 'bg-white text-purple-700 shadow-sm' : 'text-[#5B5570] hover:text-purple-700'
              }`}
              data-testid="header-toggle-business"
            >
              Business
            </button>
          </div>

          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-2 px-2 lg:flex xl:gap-3 xl:px-3" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative whitespace-nowrap font-body text-[13px] font-medium transition-colors
                  ${isActive(link.aliases)
                    ? 'text-purple-700'
                    : 'text-[#453C62] hover:text-[#1B1431]'
                  }
                  after:absolute after:-bottom-2 after:left-0 after:h-[2px] after:bg-purple-600
                  after:transition-[width] after:duration-300
                  ${isActive(link.aliases) ? 'after:w-full' : 'after:w-0 hover:after:w-full'}
                `}
              >
                {link.name}
              </Link>
            ))}

            {utilityLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className="font-body text-[13px] font-medium text-[#574D74] transition-colors hover:text-[#1B1431]"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex" data-testid="header-right-actions">
            <a
              href={SITE_PHONE_TEL}
              data-testid="header-phone-link"
              className="inline-flex h-9 items-center rounded-lg border border-purple-200 bg-white px-3 text-sm font-medium text-[#4F456C] transition-[border-color,color,background-color] hover:border-purple-400 hover:bg-purple-50 hover:text-purple-700"
            >
              <Phone size={14} className="mr-1.5" />
              {SITE_PHONE_DISPLAY}
            </a>
            <a
              href={PORTAL_LINKS.GENERIC}
              data-testid="header-login-link"
              className="inline-flex h-9 items-center rounded-lg border border-purple-300 bg-white px-3 text-sm font-semibold text-purple-700 transition-[border-color,background-color,color] hover:border-purple-500 hover:bg-purple-50"
            >
              <LogIn size={14} className="mr-1.5" />
              Login
            </a>
          </div>

          <button
            data-testid="mobile-menu-btn"
            className="rounded-full border border-purple-200 bg-white p-1.5 text-[#3D345B] transition-[background-color,color] hover:bg-purple-50 hover:text-purple-700 lg:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle mobile navigation"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

      {isOpen && (
        <div className="border-t border-purple-200 bg-[#fafafa] lg:hidden" data-testid="mobile-nav-panel">
          <nav className="flex flex-col px-4 py-4" data-testid="mobile-nav-list">
            <div className="mb-3 grid grid-cols-2 gap-2" data-testid="mobile-audience-toggle-wrap">
              <button
                type="button"
                onClick={() => setAudience('personal')}
                className={`h-11 rounded-lg px-3 text-xs font-semibold transition-[background-color,color] ${audience === 'personal' ? 'bg-purple-600 text-white' : 'bg-white text-purple-700 hover:bg-purple-50'}`}
                data-testid="mobile-toggle-personal"
              >
                Personal
              </button>
              <button
                type="button"
                onClick={() => setAudience('business')}
                className={`h-11 rounded-lg px-3 text-xs font-semibold transition-[background-color,color] ${audience === 'business' ? 'bg-purple-600 text-white' : 'bg-white text-purple-700 hover:bg-purple-50'}`}
                data-testid="mobile-toggle-business"
              >
                Business
              </button>
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`rounded-xl px-4 py-3 font-body font-medium transition-colors
                  ${isActive(link.aliases)
                    ? 'bg-purple-50 text-purple-700'
                    : 'text-[#443A60] hover:bg-purple-50 hover:text-[#1B1431]'
                  }
                `}
              >
                {link.name}
              </Link>
            ))}

            {utilityLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className="rounded-xl px-4 py-3 font-body font-medium text-[#443A60] transition-colors hover:bg-purple-50 hover:text-[#1B1431]"
              >
                {link.name}
              </Link>
            ))}

            <a
              href={SITE_PHONE_TEL}
              data-testid="mobile-phone-link"
              className="mt-4 inline-flex h-11 items-center justify-center rounded-lg border border-purple-300 bg-white px-4 text-xs font-semibold text-purple-700"
            >
              <Phone size={14} className="mr-1.5" /> Call {SITE_PHONE_DISPLAY}
            </a>

            <a
              href={PORTAL_LINKS.GENERIC}
              className="mt-3"
              data-testid="mobile-login-link"
            >
              <Button
                data-testid="mobile-login-btn"
                variant="outline"
                className="h-11 w-full rounded-lg border-purple-300 bg-white text-sm font-semibold text-purple-700 transition-[background-color] hover:bg-purple-50"
              >
                <LogIn size={16} className="mr-1" />
                Login
              </Button>
            </a>
          </nav>
        </div>
      )}

      </header>

      {showMobileSticky && !isOpen && (
        <div className="fixed bottom-2 left-2 right-2 z-[60] lg:hidden" data-testid="mobile-sticky-mini-nav">
          <div className="rounded-2xl border border-purple-200/90 bg-white/95 p-1.5 shadow-lg shadow-purple-200/35 backdrop-blur">
            <a
              href={SITE_PHONE_TEL}
              className="inline-flex h-9 w-full items-center justify-center rounded-xl border border-purple-300 bg-purple-50 px-2 text-[11px] font-semibold text-purple-700"
              data-testid="mobile-sticky-call-link"
            >
              <Phone size={13} className="mr-1" /> Call 1800 003 527
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
